import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useBootReady } from '@/lib/bootReady';
import { cn } from '@/lib/utils';
import { verifyLoginCode } from '@/lib/mfa';
import { supabase } from '@/lib/supabase';

export function LoginPage() {
  const { user, login, logout, isTwa, twaAuthFailed, twaReason, twaTelegram, isLoading, mfaPending } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [code, setCode] = useState('');

  // The HTML boot-splash fades the moment the login form is up — there's
  // nothing to load asynchronously, so once auth resolved (one way or
  // the other) we hand off immediately.
  useBootReady(!isLoading);

  // Already authenticated — возвращаем туда, куда человек шёл (см.
  // ProtectedRoute), а если некуда — на домашнюю.
  if (user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }

  // Вход по коду: пароль (или Телеграм) принят, у человека подключён код —
  // спрашиваем шесть цифр. Раньше всех прочих экранов, в том числе телеграмных.
  if (mfaPending) {
    const submitCode = async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError('');
      setIsSubmitting(true);
      const { error: codeError } = await verifyLoginCode(supabase, code);
      setIsSubmitting(false);
      if (codeError) setError(codeError);
      // Успех: сессия стала aal2, AuthProvider сам пустит внутрь.
    };
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#1a1d23' }}>
        <div className="glass-panel rounded-2xl p-8 w-full max-w-sm">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Exo 2', sans-serif" }}>
              Код входа
            </h1>
            <p className="text-white/50 text-sm mt-2">
              Для вашей учётной записи включён вход по коду. Введите шесть цифр из приложения-аутентификатора.
            </p>
          </div>
          <form onSubmit={submitCode} className="space-y-4">
            <input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              maxLength={7}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              aria-label="Код из приложения"
              className={cn(
                'w-full px-4 py-3 rounded-xl text-white text-center text-2xl tracking-widest placeholder-white/30',
                'bg-white/5 border border-white/10',
                'focus:outline-none focus:ring-2 focus:ring-[#0abab5]/50 focus:border-[#0abab5]/50'
              )}
            />
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            <button
              type="submit"
              disabled={isSubmitting}
              className={cn(
                'w-full py-3 px-6 rounded-xl font-semibold text-[#1a1d23] transition-all duration-200',
                'bg-[#0abab5] hover:bg-[#0abab5]/80',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {isSubmitting ? 'Проверяем…' : 'Подтвердить'}
            </button>
            <button
              type="button"
              onClick={() => { setCode(''); setError(''); void logout(); }}
              className="w-full py-3 px-6 rounded-xl text-white/60"
            >
              Войти другой учётной записью
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Inside Telegram WebView — show silent spinner while initData → Supabase
  // exchange runs in AuthContext. Email/password is intentionally hidden
  // because Telegram sandbox breaks Supabase redirect-based flows.
  if (isTwa && (isLoading || !twaAuthFailed)) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-6 gap-4"
        style={{ backgroundColor: '#1a1d23' }}
      >
        <div className="w-10 h-10 rounded-full border-2 border-white/10 border-t-[#0abab5] animate-spin" />
        <p className="text-white/60 text-sm">Авторизация через Telegram...</p>
      </div>
    );
  }

  // ВАС НЕТ СРЕДИ СОТРУДНИКОВ (25.08.2026). Отдельный экран, а не «что-то
  // пошло не так»: раньше на этом месте посторонний молча получал НОВУЮ
  // пустую учётную запись и пустое рабочее место, в котором нельзя работать.
  // Теперь вход честно отказывает и объясняет, что делать.
  if (isTwa && twaAuthFailed && twaReason === 'not_registered') {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-6 text-center"
        style={{ backgroundColor: '#1a1d23' }}
      >
        <div className="glass-panel rounded-2xl p-8 w-full max-w-sm flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center border border-[#0abab5]/30 bg-[#0abab5]/10">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0abab5" strokeWidth="1.8" strokeLinecap="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v5" />
              <path d="M12 16.5v.01" />
            </svg>
          </div>
          <h1 className="text-white text-xl font-semibold" style={{ fontFamily: "'Exo 2', sans-serif" }}>
            Вы ещё не сотрудник
          </h1>
          <p className="text-white/60 text-sm leading-relaxed">
            Этого аккаунта Telegram нет среди сотрудников компании, поэтому
            войти не получится. Учётную запись создаёт руководитель, когда
            оформляет вас в системе.
          </p>
          {twaTelegram && (
            <p className="text-white/40 text-xs">
              Ваш Telegram:{' '}
              <span className="text-[#0abab5] font-medium">{twaTelegram}</span>
            </p>
          )}
          <p className="text-white/40 text-xs leading-relaxed">
            Передайте это имя руководителю — он добавит вас, и вход заработает
            сам собой. Инструкция есть в боте.
          </p>
        </div>
      </div>
    );
  }

  if (isTwa && twaAuthFailed) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-6 gap-3 text-center"
        style={{ backgroundColor: '#1a1d23' }}
      >
        <p className="text-white text-lg">Не удалось войти через Telegram</p>
        <p className="text-white/50 text-sm max-w-xs">
          Закройте окно и попробуйте открыть приложение из бота заново.
        </p>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const { error: loginError } = await login(email, password);
      if (loginError) {
        setError(loginError.message || 'Invalid email or password');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: '#1a1d23' }}
    >
      <div className={cn('glass-panel rounded-2xl p-8 w-full max-w-sm')}>
        {/* Branding */}
        <div className="text-center mb-8">
          <h1
            className="text-3xl font-bold text-white"
            style={{ fontFamily: "'Exo 2', sans-serif" }}
          >
            Assistentus
          </h1>
          <p className="text-white/50 text-sm mt-2">Sign in to continue</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com"
              className={cn(
                'w-full px-4 py-3 rounded-xl text-white placeholder-white/30',
                'bg-white/5 border border-white/10',
                'focus:outline-none focus:ring-2 focus:ring-[#0abab5]/50 focus:border-[#0abab5]/50',
                'transition-all duration-200'
              )}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-1" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
              className={cn(
                'w-full px-4 py-3 rounded-xl text-white placeholder-white/30',
                'bg-white/5 border border-white/10',
                'focus:outline-none focus:ring-2 focus:ring-[#0abab5]/50 focus:border-[#0abab5]/50',
                'transition-all duration-200'
              )}
            />
          </div>

          {/* Error message */}
          {error && (
            <p className="text-red-400 text-sm text-center">{error}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className={cn(
              'w-full py-3 px-6 rounded-xl font-semibold text-[#1a1d23] transition-all duration-200',
              'bg-[#0abab5] hover:bg-[#0abab5]/80',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'focus:outline-none focus:ring-2 focus:ring-[#0abab5]/50'
            )}
          >
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
